/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  faContactCard,
  faPen,
  faPlus,
  faRotate,
} from "@fortawesome/free-solid-svg-icons";
import {
  Dropdown,
  DropdownButton,
  Modal,
  OverlayTrigger,
  Popover,
  Tab,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { proxyAPI } from "../proxyAPI.ts";
import ContactCard from "../Contacts/ContactCard";
import FullScreenLoadingIndicator from "../Common/FullScreenLoadingIndicator";
import RealTimeReportingIcon from "../../assets/icons/realtime_reporting.svg2";
import Subheading from "../Common/Subheading";
import Report from "./RealtimeReporting/Report";
import BoomLogicWFMessaging from "../Dashboard/BoomLogicWFMessaging";
import BroadcastMessaging from "../Dashboard/BroadcastMessaging";
import InboundCampaignMessaging from "../Dashboard/InboundCampaignMessaging";
import HelpButton from "../HelpContent/HelpButton";

function Spinner() {
  return (
    <div
      id="nprogress"
      style={{
        zIndex: 100,
        position: "absolute",
        top: "calc(50% - 40px)",
        left: "calc(50% - 40px)",
      }}
    >
      <div className="spinner-icon" />
    </div>
  );
}

const reportTabs = [
  { value: "transactions", label: "Transactions" },
  { value: "wf", label: "Logic messaging" },
  { value: "broadcast", label: "Broadcast messaging" },
  { value: "inbound", label: "Inbound triggers" },
];

export const ContactPopover = React.forwardRef(
  ({ popper, children, show: _, ...props }, ref) => {
    const [contact, setContact] = useState(null);

    const getContact = async () => {
      const data = await proxyAPI({
        prefix: "contacts/v1",
        url: `/first/${props.uuid}?fields.cards=1&fields.channels=1`,
      });
      setContact(data);
    };

    useEffect(() => {
      getContact();
    }, []);

    const contactFields =
      contact?.details
        .filter((d) => !d.field.isSms && !d.field.isEmail && !d.field.isVoice)
        .filter((d) => d.values[0].data || d.values[0].option.label)
        .slice(0, 2)
        .map((d) => d.field) ?? [];

    useEffect(() => {
      popper.scheduleUpdate();
    }, [contact, popper]);

    return (
      <Popover ref={ref} body {...props}>
        <div className="overflow-scroll">
          <table>
            <thead>
              <tr>
                <th className="px-2" />
                <th className="px-2" />

                {contactFields.map((cf) => (
                  <th className="px-2" key={cf.uuid}>
                    {cf.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {contact && (
                <ContactCard
                  contact={contact}
                  // eslint-disable-next-line react/no-unstable-nested-components
                  contactButton={() => (
                    <Link to={`/assets/contacts/edit/${props.uuid}`}>
                      <FontAwesomeIcon icon={faPen} />
                    </Link>
                  )}
                  contactFields={contactFields}
                  fullView={false}
                />
              )}
            </tbody>
          </table>
        </div>
      </Popover>
    );
  }
);

export function contactOption({ selectOption, ...props }) {
  return (
    <div className="border-bottom">
      <table>
        <tbody>
          <ContactCard
            contact={props.data.contact}
            contactFields={props.data.contact.details
              .filter(
                (d) => !d.field.isSms && !d.field.isEmail && !d.field.isVoice
              )
              .filter((d) => d.values[0].data || d.values[0].option.label)
              .slice(0, 2)
              .map((d) => d.field)}
            contactButton={() => (
              // eslint-disable-next-line jsx-a11y/control-has-associated-label
              <button
                type="button"
                className="btn btn-outline-primary border-0"
                onClick={() => selectOption(props.data)}
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
            )}
          />
        </tbody>
      </table>
    </div>
  );
}

export function contactMenuList(props) {
  return (
    <div style={{ ...props.style, height: "100%", overflow: "auto" }}>
      <table className="table table-condensed bg-white">
        <tbody>{props.children}</tbody>
      </table>
    </div>
  );
}

export default function RealtimeReportingPage() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [topics, setTopics] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [filterTopics, setFilterTopics] = useState([]);
  const [filterTemplates, setFilterTemplates] = useState([]);
  const [filterContacts, setFilterContacts] = useState([]);
  const [filterTickets, setFilterTickets] = useState([]);
  const [dateLTE, setDateLTE] = useState(null);
  const [dateGTE, setDateGTE] = useState(null);

  const getEntries = async (page = 0, perPage = 10) => {
    setLoading(true);
    const data = await proxyAPI({
      prefix: "logic/reporting",
      url: `entries?pagination.perPage=${perPage}&pagination.page=${page}${
        filterTopics.length > 0
          ? `&filters.topics=${filterTopics.join("&filters.topics=")}`
          : ""
      }${
        filterTemplates.length > 0
          ? `&filters.template_uuids=${filterTemplates.join(
              "&filters.template_uuids="
            )}`
          : ""
      }${
        filterTickets.length > 0
          ? `&filters.tickets=${filterTickets.join("&filters.tickets=")}`
          : ""
      }${
        filterContacts.length > 0
          ? `&filters.contact_uuids=${filterContacts.join(
              "&filters.contact_uuids="
            )}`
          : ""
      }${dateGTE ? `&filters.date_gte=${dateGTE}` : ""}${
        dateLTE ? `&filters.date_lte=${dateLTE}` : ""
      }`,
    });
    setTimeout(() => {
      setLoading(false);
      if (data?.data) {
        setEntries(data.data);
        setTotal(data.info.total);
      }
    }, 500);
  };

  useEffect(() => {
    (async () => {
      const topicsData = await proxyAPI({
        prefix: "logic/reporting",
        url: `topics`,
      });
      if (topicsData) {
        setTopics(topicsData.data);
      }
      const templatesData = await proxyAPI({
        prefix: "logic/reporting",
        url: `templates`,
      });
      if (templatesData) {
        setTemplates(templatesData.data);
      }
    })();
  }, []);

  const { realtimeTab = "transactions" } = useParams();
  // get the URL parameters
  const [sp, setSP] = useSearchParams();
  const { projectUUID, broadcastUUID, inboundUUID, startDate, endDate } =
    Object.fromEntries(sp.entries());

  const navigate = useNavigate();

  return (
    <div>
      <FullScreenLoadingIndicator loading={loading} />
      <div className="d-flex justify-content-between">
        <div className="d-flex align-items-center">
          <Subheading
            Icon={RealTimeReportingIcon}
            heading="Real-time Messaging Data"
            subheading="Select and interrogate messaging data."
          />
        </div>
        {/* <div className="d-flex justify-content-end">
          <WhiteButtonsContainer
            buttons={[
              {
                icon: ContactsIcon,
                as: "RRLink",
                heading: "Topics",
                subheading: "Manage",
                to: "/reporting/topics",
              },
              {
                icon: ContactsIcon,
                as: "RRLink",
                heading: "Templates",
                subheading: "Manage",
                to: "/reporting/templates",
              },
            ]}
          />
        </div> */}
      </div>

      <div className="mb-3">
        <DropdownButton
          id="dropdown-basic-button"
          title={reportTabs.find((tab) => tab.value === realtimeTab).label}
          variant="colored"
        >
          {reportTabs.map((tab) => (
            <Dropdown.Item
              as="button"
              key={tab.value}
              eventKey={tab.value}
              onClick={() => {
                navigate(`/reporting/realtime/${tab.value}`);
              }}
            >
              {tab.label}
            </Dropdown.Item>
          ))}
        </DropdownButton>
      </div>

      <Tab.Container
        activeKey={realtimeTab}
        id="controlled-report-tab"
        animation={false}
        mountOnEnter
        unmountOnExit
      >
        <Tab.Content>
          <Tab.Pane eventKey="wf">
            <div style={{ padding: "1px", position: "relative" }}>
              {
                // show spinner when loading
                loading.table && <Spinner />
              }
              <div className="p-3 rounded-3 bg-white">
                <BoomLogicWFMessaging showTransactions />
              </div>
            </div>
          </Tab.Pane>
          <Tab.Pane eventKey="broadcast">
            <div style={{ padding: "1px", position: "relative" }}>
              {
                // show spinner when loading
                loading.table && <Spinner />
              }
              <div className="p-3 rounded-3 bg-white">
                <BroadcastMessaging showTransactions />
              </div>
            </div>
          </Tab.Pane>
          <Tab.Pane eventKey="inbound">
            <div style={{ padding: "1px", position: "relative" }}>
              {
                // show spinner when loading
                loading.table && <Spinner />
              }
              <div className="p-3 rounded-3 bg-white">
                <InboundCampaignMessaging showTransactions />
              </div>
            </div>
          </Tab.Pane>
          <Tab.Pane
            eventKey="transactions"
            title="Report summary"
            className="padding-top-80 padding-bottom-80"
          >
            <Report />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
      {(projectUUID || inboundUUID || broadcastUUID) && (
        <Modal show onHide={() => setSP(new URLSearchParams())} size="xxl">
          <Modal.Header closeButton>
            <Modal.Title>Detailed Report</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Report
              hideFilters
              projectUUID={projectUUID}
              broadcastUUID={broadcastUUID}
              inboundUUID={inboundUUID}
              startDate={startDate}
            />
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
}
